import React, { type FC, type ReactNode } from "react";
import { cn } from "@heroui/react";

import styles from "./HoverGlow.module.css";

interface Props {
    children: ReactNode;
    /** When false (e.g. a disabled control), the rim is omitted so nothing glows on hover. */
    isActive?: boolean;
    /** Shimmer the leading icon through bright/light tints instead of the saturated palette - for
     *  controls with a coloured background and a white icon, where the default would lose contrast. */
    brightIcon?: boolean;
    /** Hero-only delight: a soft breathing aura behind the control plus four small stars that revolve
     *  on offset elliptical orbits above it and twinkle out of phase - a gentle firework. Opt-in;
     *  reserve for a single hero CTA. Decorative only (aria-hidden, pointer-events:none) and
     *  animation-paused until hover/focus, so idle cost is zero. Respects prefers-reduced-motion
     *  (calm static aura, no flying particles). Pair with `relative z-2` on the wrapped control. */
    sparkle?: boolean;
    className?: string;
}

/**
 * Wraps a pill-shaped control (HeroUI Button etc.) with a cool->warm rim glow that fades in on
 * hover / keyboard focus - the CSS-triggered sibling of ChainEditButton's timed glow.
 *
 * The wrapped control must sit above the rim layers: give it `relative z-2`.
 */
export const HoverGlow: FC<Props> = ({ children, isActive = true, brightIcon = false, sparkle = false, className }) => (
    <span className={cn(styles.wrapper, brightIcon && styles.bright, sparkle && styles.sparkleOn, className)}>
        {isActive && sparkle && <span aria-hidden className={styles.sparkleAura} />}
        {isActive && (
            <>
                <span aria-hidden className={styles.glow} />
                <span aria-hidden className={styles.ring} />
            </>
        )}
        {children}
        {isActive && sparkle && (
            <span aria-hidden className={styles.constellation}>
                <span className={cn(styles.star, styles.s1)} />
                <span className={cn(styles.star, styles.s2)} />
                <span className={cn(styles.star, styles.s3)} />
                <span className={cn(styles.star, styles.s4)} />
            </span>
        )}
    </span>
);
